import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Edit2 } from 'lucide-react';


interface ToolSettingsEditorProps {
    selectedTools: string[];
    toolSettings: Record<string, Record<string, unknown>>;
    onSettingsChange: (settings: Record<string, Record<string, unknown>>) => void;
}

export const ToolSettingsEditor: React.FC<ToolSettingsEditorProps> = ({
    selectedTools,
    toolSettings,
    onSettingsChange,
}) => {
    const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set());
    const [editingKeys, setEditingKeys] = useState<Record<string, Record<string, { key: string; value: string }>>>({});

    // Initialize settings for newly selected tools
    useEffect(() => {
        const updatedSettings = { ...toolSettings };
        let hasChanges = false;

        selectedTools.forEach((toolName) => {
            if (!updatedSettings[toolName]) {
                updatedSettings[toolName] = {};
                hasChanges = true;
            }
        });

        // Remove settings for tools that are no longer selected
        Object.keys(updatedSettings).forEach((toolName) => {
            if (!selectedTools.includes(toolName)) {
                delete updatedSettings[toolName];
                hasChanges = true;
            }
        });

        if (hasChanges) {
            onSettingsChange(updatedSettings);
        }
    }, [selectedTools, toolSettings, onSettingsChange]);

    const toggleToolExpansion = (toolName: string): void => {
        const newExpanded = new Set(expandedTools);
        if (newExpanded.has(toolName)) {
            newExpanded.delete(toolName);
        } else {
            newExpanded.add(toolName);
        }
        setExpandedTools(newExpanded);
    };

    const getToolLabel = (toolId: string): string => {
        // Try to extract a readable label from the tool ID
        return toolId
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const updateSetting = (toolName: string, key: string, value: unknown): void => {
        const updatedSettings = {
            ...toolSettings,
            [toolName]: {
                ...toolSettings[toolName],
                [key]: value,
            },
        };
        onSettingsChange(updatedSettings);
    };

    const startEditingKey = (toolName: string, oldKey: string, value: unknown): void => {
        setEditingKeys({
            ...editingKeys,
            [toolName]: {
                ...(editingKeys[toolName] || {}),
                [oldKey]: {
                    key: oldKey,
                    value: String(value),
                },
            },
        });
    };

    const cancelEditingKey = (toolName: string, key: string): void => {
        const updated = { ...editingKeys };
        if (updated[toolName]) {
            delete updated[toolName][key];
        }
        setEditingKeys(updated);
    };

    const saveEditingKey = (toolName: string, oldKey: string): void => {
        const editing = editingKeys[toolName]?.[oldKey];
        if (!editing) return;

        const newKey = editing.key.trim();
        const newValue = editing.value;

        if (newKey && newKey !== oldKey) {
            // Remove old key and add new key
            const updatedSettings = {
                ...toolSettings,
                [toolName]: {
                    ...(Object.fromEntries(
                        Object.entries(toolSettings[toolName] || {}).filter(([k]) => k !== oldKey)
                    ) || {}),
                    [newKey]: parseValue(newValue),
                },
            };
            onSettingsChange(updatedSettings);
        } else if (newKey === oldKey) {
            // Just update the value
            updateSetting(toolName, oldKey, parseValue(newValue));
        }

        cancelEditingKey(toolName, oldKey);
    };

    const parseValue = (value: string): unknown => {
        // Try to parse as JSON (number, boolean, object, array)
        try {
            return JSON.parse(value);
        } catch {
            // If not valid JSON, return as string
            return value;
        }
    };

    const formatValue = (value: unknown): string => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') {
            return JSON.stringify(value, null, 2);
        }
        return String(value);
    };

    const getInputStyles = () => ({
        borderColor: "hsl(var(--muted-foreground))",
    });

    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        e.target.style.borderColor = "hsl(var(--primary))";
        e.target.style.boxShadow = "0 0 0 2px hsl(var(--primary) / 0.2)";
    };

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        e.target.style.borderColor = "hsl(var(--muted-foreground))";
        e.target.style.boxShadow = 'none';
    };

    if (selectedTools.length === 0) {
        return null;
    }

    return (
        <div className="space-y-4">
            <h3 className="text-sm font-medium mb-3 text-foreground">
                Tool Settings
            </h3>
            <div className="space-y-3">
                {selectedTools.map((toolName) => {
                    const isExpanded = expandedTools.has(toolName);
                    const toolSettingsForTool = toolSettings[toolName] || {};
                    const settingsEntries = Object.entries(toolSettingsForTool);

                    return (
                        <div
                            key={toolName}
                            className="border rounded-lg overflow-hidden"
                            style={{ borderColor: "hsl(var(--muted-foreground))" }}
                        >
                            <button
                                type="button"
                                onClick={() => toggleToolExpansion(toolName)}
                                className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                                style={{
                                    backgroundColor: isExpanded
                                        ? "hsl(var(--primary) / 0.1)"
                                        : 'transparent',
                                }}
                            >
                                <div className="flex items-center gap-2">
                                    {isExpanded ? (
                                        <ChevronUp size={18} className="text-primary" />
                                    ) : (
                                        <ChevronDown size={18} className="text-primary" />
                                    )}
                                    <span className="font-medium text-foreground">
                                        {getToolLabel(toolName)}
                                    </span>
                                    {settingsEntries.length > 0 && (
                                        <span
                                            className="text-xs px-2 py-0.5 rounded-full"
                                            style={{
                                                backgroundColor: "hsl(var(--primary))",
                                                color: "hsl(var(--primary-foreground))",
                                            }}
                                        >
                                            {settingsEntries.length} {settingsEntries.length === 1 ? 'setting' : 'settings'}
                                        </span>
                                    )}
                                </div>
                            </button>

                            {isExpanded && (
                                <div className="p-4 border-t" style={{ borderColor: "hsl(var(--muted-foreground))" }}>
                                    {settingsEntries.length === 0 ? (
                                        <div className="text-sm mb-4 text-muted-foreground">
                                            No settings configured for this tool.
                                        </div>
                                    ) : (
                                        <div className="space-y-3 mb-4">
                                            {settingsEntries.map(([key, value]) => {
                                                const isEditing = !!editingKeys[toolName]?.[key];
                                                const editing = editingKeys[toolName]?.[key];

                                                return (
                                                    <div
                                                        key={key}
                                                        className="border rounded-lg p-3"
                                                        style={{ borderColor: "hsl(var(--muted-foreground))" }}
                                                    >
                                                        {isEditing ? (
                                                            <div className="space-y-2">
                                                                <div>
                                                                    <label className="block text-xs font-medium mb-1 text-foreground">
                                                                        Setting Key
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        value={editing.key}
                                                                        onChange={(e) =>
                                                                            setEditingKeys({
                                                                                ...editingKeys,
                                                                                [toolName]: {
                                                                                    ...(editingKeys[toolName] || {}),
                                                                                    [key]: { ...editing, key: e.target.value },
                                                                                },
                                                                            })
                                                                        }
                                                                        className="w-full px-2 py-1.5 border rounded text-sm outline-none transition-colors"
                                                                        style={getInputStyles()}
                                                                        onFocus={handleInputFocus}
                                                                        onBlur={handleInputBlur}
                                                                        placeholder="Setting key"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label className="block text-xs font-medium mb-1 text-foreground">
                                                                        Setting Value (JSON or string)
                                                                    </label>
                                                                    <textarea
                                                                        value={editing.value}
                                                                        onChange={(e) =>
                                                                            setEditingKeys({
                                                                                ...editingKeys,
                                                                                [toolName]: {
                                                                                    ...(editingKeys[toolName] || {}),
                                                                                    [key]: { ...editing, value: e.target.value },
                                                                                },
                                                                            })
                                                                        }
                                                                        className="w-full px-2 py-1.5 border rounded text-sm font-mono outline-none transition-colors"
                                                                        style={getInputStyles()}
                                                                        onFocus={handleInputFocus}
                                                                        onBlur={handleInputBlur}
                                                                        rows={3}
                                                                        placeholder='e.g., "value", 123, true, {"key": "value"}'
                                                                    />
                                                                </div>
                                                                <div className="flex justify-end gap-2">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => cancelEditingKey(toolName, key)}
                                                                        className="px-3 py-1 text-xs border rounded transition-colors"
                                                                        style={{
                                                                            color: "hsl(var(--foreground))",
                                                                            borderColor: "hsl(var(--muted-foreground))",
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            e.currentTarget.style.backgroundColor = "hsl(var(--muted))";
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                                        }}
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => saveEditingKey(toolName, key)}
                                                                        className="px-3 py-1 text-xs rounded transition-colors"
                                                                        style={{
                                                                            backgroundColor: "hsl(var(--primary))",
                                                                            color: "hsl(var(--primary-foreground))",
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            e.currentTarget.style.backgroundColor = "hsl(var(--secondary))";
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.currentTarget.style.backgroundColor = "hsl(var(--primary))";
                                                                        }}
                                                                    >
                                                                        Save
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-start justify-between gap-2">
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2 mb-1">
                                                                        <span className="text-xs font-semibold text-primary">
                                                                            {key}:
                                                                        </span>
                                                                    </div>
                                                                    <div
                                                                        className="text-sm font-mono p-2 rounded bg-opacity-10"
                                                                        style={{
                                                                            backgroundColor: "hsl(var(--muted) / 0.1)",
                                                                            color: "hsl(var(--foreground))",
                                                                        }}
                                                                    >
                                                                        {formatValue(value)}
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => startEditingKey(toolName, key, value)}
                                                                        className="p-1 transition-colors text-primary"
                                                                        onMouseEnter={(e) => {
                                                                            e.currentTarget.style.color = "hsl(var(--secondary))";
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.currentTarget.style.color = "hsl(var(--primary))";
                                                                        }}
                                                                        title="Edit setting"
                                                                    >
                                                                        <Edit2 size={14} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* <button
                                        type="button"
                                        onClick={() => addNewSetting(toolName)}
                                        className="w-full py-2 px-3 border border-dashed rounded-lg flex items-center justify-center gap-2 transition-colors text-sm"
                                        style={{
                                            borderColor: "hsl(var(--muted-foreground))",
                                            color: "hsl(var(--foreground))",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = "hsl(var(--primary) / 0.1)";
                                            e.currentTarget.style.borderColor = "hsl(var(--primary))";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.borderColor = colors['neutral-1'];
                                        }}
                                    >
                                        <Plus size={14} />
                                        Add Setting
                                    </button> */}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

